import React, { useRef, useState } from "react";
import { Modal } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import ServiceEditor from "./ServiceEditor";
import PageLoader from "../../../../common/ui/PageLoader";
import ScrollableWrapper from "../common/ScrollableWrapper";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import SecondaryButton from "../../../../common/ui/SecondaryButton";
import { validateStep3 } from "./LogicBuilderStep3";
import ValidationErrorDisplay from "../common/ValidationErrorDisplay";

interface EditServiceModalProps {
  open: boolean;
  service: any | null;
  onClose: () => void;
  onSave: (updatedService: any) => Promise<void>;
  onDelete?: (service: any) => void;
  canDelete?: boolean;
  type: string;
}

const EditServiceModal = ({
  open,
  service,
  onClose,
  onSave,
  onDelete,
  canDelete = true,
  type,
}: EditServiceModalProps) => {
  const theme: Theme = useTheme();
  const [editedService, setEditedService] = useState<any>(service);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const scrollableRef = useRef<HTMLDivElement>(null);

  // Update editedService when service prop changes
  React.useEffect(() => {
    if (service) {
      setEditedService(service);
      setValidationErrors([]); // Clear errors when service changes
    }
  }, [service]);

  const handleServiceChange = (updatedService: any) => {
    setEditedService(updatedService);
    setValidationErrors([]); // Clear errors when service is edited
  };

  const handleSave = async () => {
    if (!editedService) return;

    // Validate using validateStep3 for both types
    // Pass original service for edit mode comparison
    const validation = validateStep3(
      [editedService],
      type,
      service ? [service] : undefined
    );
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editedService);
      onClose();
    } catch (error) {
      console.error("Error saving service:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    if (onDelete && editedService) {
      onDelete(editedService);
      onClose();
    }
  };

  if (!service) return null;

  return (
    <Modal open={open} onClose={onClose} disableEnforceFocus>
      <div style={styles.modalContainer(theme)}>
        {/* Modal Header */}
        <div style={styles.modalHeaderContainer}>
          <div style={styles.modalHeaderTitle(theme)}>Edit service logic</div>
          <div onClick={onClose} style={styles.iconWrapper}>
            <XIconSvg />
          </div>
        </div>

        {/* Modal Content */}
        {isSaving ? (
          <div style={styles.loaderContainer}>
            <PageLoader />
          </div>
        ) : editedService ? (
          <>
            <div style={styles.contentWithScroll(theme)}>
              <ScrollableWrapper ref={scrollableRef} customPadding={"0px"}>
                <ServiceEditor
                  service={editedService}
                  onServiceChange={handleServiceChange}
                  type={type}
                />
              </ScrollableWrapper>
            </div>

            {/* Validation Errors Display */}
            <ValidationErrorDisplay errors={validationErrors} />

            {/* Footer with Save and Delete buttons */}
            <div style={styles.footer}>
              {onDelete && canDelete && (
                <div style={{ flex: 1 }}>
                  <PrimaryButton
                    label={"Delete"}
                    onClick={handleDelete}
                    sx={{
                      backgroundColor: theme.colorTokens.D[50],
                      "&:hover": {
                        backgroundColor: theme.colorTokens.D[60],
                      },
                    }}
                  />
                </div>
              )}
              <PrimaryButton label={"Save"} onClick={handleSave} />
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export default EditServiceModal;

const styles = {
  modalContainer: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "24px",
    width: "80%",
    minWidth: "400px",
    maxWidth: "1113px",
    height: "90vh",
    borderRadius: "8px",
    boxShadow: "var(--box-shadow-general)",
    gap: "32px",
    outline: "none",
  }),
  modalHeaderContainer: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
  },
  modalHeaderTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "34px",
    flex: 1,
  }),
  iconWrapper: {
    marginTop: "10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loaderContainer: {
    height: "561px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  contentWithScroll: (theme: Theme) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
  }),
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "16px",
  },
};
